/* eslint-disable @typescript-eslint/naming-convention */
import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  GetSoApprovalRequestType,
  GetSoApprovalResponseType,
  GetUsersResponseType,
  SupplyOrderApprovalDetailV2ItemType,
} from 'utils/apiList/supplyOrderApproval'
import { withLoadingReducer } from 'utils/reducerHandler'
import { LocationListType } from 'features/Location/@types/location'
import {
  fetchGetSupplyOrderApproval,
  fetchGetUsers,
  fetchPutSupplyOrderApprovalDetail,
  fetchLocationList,
  fetchGetSupplyOrderApprovalDetailV2,
} from './supplyOrderApprovalAsyncThunk'

type FormType = {
  soPartialItemId: number
  icQuantity: number
  evidence: string
  chronology: string
  icReason: string
  lpTarget: string
}

export type OriginDestinationItemType = Pick<
  LocationListType,
  'location_id' | 'location_name' | 'location_type'
>

export type SupplyOrderApprovalSliceType = {
  isLoading: boolean
  queryData: GetSoApprovalRequestType
  pagination: {
    page_size: number
    page_index: number
    number_of_elements: number
  }
  popup: {
    isOpen: boolean
    type: 'REVIEW' | 'DETAIL'
    selected?: GetSoApprovalResponseType['data'][number]
  }
  data: GetSoApprovalResponseType['data']
  detailsV2: SupplyOrderApprovalDetailV2ItemType[]
  formV2: FormType[]
  users: GetUsersResponseType['content']
  locationList: OriginDestinationItemType[]
}

const initialState: SupplyOrderApprovalSliceType = {
  isLoading: false,
  queryData: {
    'pagination.page_index': 1,
    supply_order_no: '',
  },
  pagination: {
    page_size: 20,
    page_index: 1,
    number_of_elements: 0,
  },
  popup: {
    isOpen: false,
    type: 'REVIEW',
  },
  data: [],
  detailsV2: [],
  formV2: [],
  users: [],
  locationList: [],
}

const supplyOrderApprovalSlice = createSlice({
  name: 'supplyOrderApproval',
  initialState,
  reducers: {
    setReset: () => initialState,
    setQueryData: (state, action) => {
      state.queryData = { ...state.queryData, ...action.payload }
    },
    setOpenPopup: (
      state,
      action: PayloadAction<{
        type: SupplyOrderApprovalSliceType['popup']['type']
        selected?: SupplyOrderApprovalSliceType['popup']['selected']
      }>,
    ) => {
      const { type, selected } = action.payload

      state.popup.isOpen = true
      state.popup.type = type
      state.popup.selected = selected
    },
    setClosePopup: (state) => {
      state.popup.isOpen = false
    },
    resetPopup: (state) => {
      state.popup = initialState.popup
    },
    setFormV2: (
      state,
      action: PayloadAction<{
        index: number
        key: keyof SupplyOrderApprovalSliceType['formV2'][number]
        value: unknown
      }>,
    ) => {
      const { index, key, value } = action.payload
      state.formV2[index][key] = value as never
    },
    resetDetailsAndFormV2: (state) => {
      state.detailsV2 = initialState.detailsV2
      state.formV2 = initialState.formV2
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<SupplyOrderApprovalSliceType>) => {
      builder
        .addCase(fetchGetSupplyOrderApproval.fulfilled, (state, action) => {
          const { data, pagination } = action.payload
          state.data = data
          state.pagination = pagination
        })
        .addCase(fetchGetUsers.fulfilled, (state, action) => {
          state.users = action.payload.content
        })
        .addCase(fetchLocationList.fulfilled, (state, action) => {
          state.locationList = action.payload
        })
        .addCase(fetchGetSupplyOrderApprovalDetailV2.fulfilled, (state, action) => {
          state.detailsV2 = action.payload.data
          state.formV2 = action.payload.data.map((item) => ({
            chronology:
              item.status === 'WAITING_APPROVAL' ? item.ic_chronology : item.lp_chronology,
            evidence: item.status === 'WAITING_APPROVAL' ? item.ic_evidence : item.lp_evidence,
            icQuantity: item.ic_quantity,
            icReason: item.ic_reason,
            lpTarget: item.lp_target,
            soPartialItemId: item.so_partial_item_id,
          }))
        })
    },
    [fetchGetSupplyOrderApproval, fetchPutSupplyOrderApprovalDetail],
  ),
})

export const {
  setReset,
  setQueryData,
  setFormV2,
  resetDetailsAndFormV2,
  setOpenPopup,
  setClosePopup,
  resetPopup,
} = supplyOrderApprovalSlice.actions
export default supplyOrderApprovalSlice.reducer
